import { useState, useEffect, useRef, useCallback } from "react";
import { apiGet, apiPost } from "../lib/api.js";

export interface IngestionStatus {
	projectId: string;
	zohoProjectId: string;
	workdriveFolderId: string | null;
	total: number;
	completed: number;
	pending: number;
	failed: number;
	lastIngestedAt: string | null;
	isStale: boolean;
	hasPendingDocuments: boolean;
	ingestionBadge: "ready" | "pending" | "none";
	failedFiles: Array<{ name: string; error: string | null }>;
}

export interface UseIngestionStatusReturn {
	status: IngestionStatus | null;
	loading: boolean;
	error: string | null;
}

/**
 * Polls the ingestion status endpoint for the active project.
 *
 * - Polls every 10 seconds whilst documents are pending (active ingestion).
 * - Polls every 60 seconds when idle (no pending documents).
 * - **Auto-triggers the next ingestion batch** when pending documents are
 *   detected and no batch is currently in flight. This drives the batched
 *   ingestion pipeline forward automatically after the initial "Sync Now".
 * - Only polls when zohoProjectId is non-null AND hasFolder is true.
 * - Uses a recursive setTimeout pattern to avoid overlapping requests.
 * - Cleans up on unmount and when the project changes.
 */
export function useIngestionStatus(
	zohoProjectId: string | null,
	hasFolder: boolean,
): UseIngestionStatusReturn {
	const [status, setStatus] = useState<IngestionStatus | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Stable ref to the current status so the timeout callback can read it
	// without needing it as a dependency.
	const statusRef = useRef<IngestionStatus | null>(null);

	// Ref to the scheduled timeout — used for cleanup.
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Ref that signals the current effect instance to stop (avoids stale closures
	// after unmount or project change).
	const cancelledRef = useRef(false);

	// Tracks whether a batch trigger request is currently in flight,
	// preventing duplicate POST /ingest calls during rapid polling.
	const batchInFlightRef = useRef(false);

	const clearScheduledTimeout = useCallback(() => {
		if (timeoutRef.current !== null) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	useEffect(() => {
		// Nothing to poll if no project selected or no folder linked.
		if (!zohoProjectId || !hasFolder) {
			setStatus(null);
			setError(null);
			setLoading(false);
			return;
		}

		cancelledRef.current = false;
		batchInFlightRef.current = false;

		async function fetchStatus(): Promise<void> {
			if (cancelledRef.current) return;

			try {
				const response = await apiGet(`/api/projects/${zohoProjectId}/ingestion`);

				if (cancelledRef.current) return;

				if (response.ok) {
					const raw = (await response.json()) as IngestionStatus;
					// Back-compat: ensure failedFiles is always an array.
					const data: IngestionStatus = {
						...raw,
						failedFiles: raw.failedFiles ?? [],
					};
					statusRef.current = data;
					setStatus(data);
					setError(null);

					// Auto-trigger the next batch if there are pending documents
					// and no batch request is already in flight.
					if (data.hasPendingDocuments && !batchInFlightRef.current) {
						batchInFlightRef.current = true;
						apiPost(`/api/projects/${zohoProjectId}/ingest`, {})
							.catch((err) => {
								console.error("[useIngestionStatus] Failed to trigger next batch:", err);
							})
							.finally(() => {
								setTimeout(() => {
									batchInFlightRef.current = false;
								}, 15_000);
							});
					}
				} else {
					const errorData = await response
						.json()
						.catch(() => null) as { message?: string } | null;
					setError(
						errorData?.message ||
							`Failed to load ingestion status (${response.status})`,
					);
				}
			} catch {
				if (!cancelledRef.current) {
					setError("Failed to connect to the server");
				}
			} finally {
				if (!cancelledRef.current) {
					setLoading(false);

					// Schedule the next poll. Use 10s if actively ingesting, 60s if idle.
					const currentStatus = statusRef.current;
					const intervalMs =
						currentStatus?.hasPendingDocuments ? 10_000 : 60_000;

					timeoutRef.current = setTimeout(() => {
						if (!cancelledRef.current) {
							fetchStatus();
						}
					}, intervalMs);
				}
			}
		}

		// Kick off immediately.
		setLoading(true);
		fetchStatus();

		return () => {
			cancelledRef.current = true;
			clearScheduledTimeout();
		};
	}, [zohoProjectId, hasFolder, clearScheduledTimeout]);

	return { status, loading, error };
}
